import type { Alignment, StationEquation } from '@/core/alignment';
import type { AlignmentInput } from '@/graphql/graphql';

/**
 * FNV-1a 64-bit, hex. Chosen over SHA-256 because the hash is computed
 * synchronously during render (the dialog's live estimate, the dock's
 * "alignment changed" check) and `crypto.subtle.digest` is async and absent
 * from jsdom. This is a provenance fingerprint, never a security primitive.
 */
const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK64 = 0xffffffffffffffffn;

export function fnv1a64Hex(text: string): string {
  let h = FNV_OFFSET;
  for (let i = 0; i < text.length; i++) {
    h ^= BigInt(text.charCodeAt(i) & 0xff);
    h = (h * FNV_PRIME) & MASK64;
  }
  return h.toString(16).padStart(16, '0');
}

/** Quantise to 1 mm so float noise from re-tessellation doesn't change the hash. */
const q = (v: number): string => Math.round(v * 1000).toString();

/**
 * Which part of the alignment a run's fingerprint has to cover.
 *
 * `FULL` — the whole polyline, Z included. The default, and the right answer
 * for anything that measures FROM the centreline: clearance takes its datum
 * from the alignment's own elevation (`scripts/clearance.py`: `datum =
 * elevation_at_stations(...) + verticalOffsetM`), so moving the centreline up
 * or down moves every number it produced.
 *
 * `PLAN` — the horizontal geometry only. For runs that station in 2D and take
 * every elevation from the compared surfaces: station volumes on the deviation
 * and surface-difference paths, and the layer stack. There,
 * `alignment_geom.py::cum_lengths` stations by `hypot(dx, dy)` with Z
 * explicitly ignored, `project_points` is a pure XY projection, and no worker
 * reads the centreline's Z at all (`station_bins.py` imports
 * `elevation_at_stations` only to re-export it). A Z-only change cannot move a
 * single number in `station_volumes.json`, so hashing Z there would mark
 * finished runs stale over nothing — most visibly when a flat LandXML
 * alignment (`zSource: 'NONE'`, Z = 0 throughout) is draped onto a surface to
 * give the 3D corridor something to sit on.
 *
 * The mode is NOT recorded in the digest and cannot be recovered from it, so a
 * run must be compared in the mode it was stamped in. `FULL` is the default so
 * that a new caller which forgets to think about this gets the strict answer.
 *
 * One-time cost of `PLAN`: a station-volume run stored before it existed was
 * stamped with a Z-inclusive hash, so it reads as stale once. Re-running
 * reproduces the same volumes and settles it.
 */
export type AlignmentHashMode = 'FULL' | 'PLAN';

export function alignmentSourceHash(
  a: Pick<Alignment, 'points' | 'staStart' | 'equations'>,
  mode: AlignmentHashMode = 'FULL',
): string {
  const parts: string[] = [q(a.staStart)];
  for (const e of [...a.equations].sort((x, y) => x.staInternal - y.staInternal)) {
    parts.push(`${q(e.staInternal)}:${q(e.staAhead)}`);
  }
  // Distinct separators, so the two modes cannot collide on an alignment whose
  // Z happens to read like a coordinate.
  parts.push(mode === 'PLAN' ? '|xy' : '|');
  for (let i = 0; i + 2 < a.points.length; i += 3) {
    parts.push(q(a.points[i]), q(a.points[i + 1]));
    if (mode === 'FULL') parts.push(q(a.points[i + 2]));
  }
  return fnv1a64Hex(parts.join(','));
}

/**
 * Concrete (non-optional) shape of what `toAlignmentInput` builds. Every
 * field's type is a subtype of the generated `AlignmentInput` (Task 5) —
 * e.g. `equations`/`points` are always present, non-nullable arrays here,
 * where the GraphQL input allows them to be omitted — so a
 * `AlignmentInputPayload` value is always directly assignable to
 * `AlignmentInput` at a mutation call site without casting or remapping.
 */
export interface AlignmentInputPayload {
  name: string;
  key: string;
  layerId: string | null;
  staStart: number;
  equations: StationEquation[];
  /** Plain float64 numbers — never a typed array, never stringified (R9). */
  points: number[][];
  sourceHash: string;
}

// Compile-time-only check that the payload really is assignable to the
// generated mutation input type — if `AlignmentInput` (Task 5's codegen)
// ever grows a new required field, this line fails to compile instead of
// silently drifting. Never called.
const _assignableToAlignmentInput = (p: AlignmentInputPayload): AlignmentInput => p;
void _assignableToAlignmentInput;

/** `mode` picks which fingerprint travels with the run — see `AlignmentHashMode`. */
export function toAlignmentInput(a: Alignment, mode: AlignmentHashMode = 'FULL'): AlignmentInputPayload {
  const points: number[][] = [];
  for (let i = 0; i + 2 < a.points.length; i += 3) {
    points.push([a.points[i], a.points[i + 1], a.points[i + 2]]);
  }
  return {
    name: a.name,
    key: a.key,
    layerId: a.layerId || null,
    staStart: a.staStart,
    equations: [...a.equations].sort((x, y) => x.staInternal - y.staInternal),
    points,
    sourceHash: alignmentSourceHash(a, mode),
  };
}

/**
 * True when the live registry alignment still matches the one the analysis
 * ran on. An unregistered alignment (`undefined`) returns true: there is
 * nothing to contradict, and the dock has a separate "not registered" state.
 */
export function alignmentHashMatches(
  meta: { sourceHash: string },
  live: Alignment | undefined,
  mode: AlignmentHashMode = 'FULL',
): boolean {
  if (!live) return true;
  // Must be the SAME mode the run was stamped in (see `AlignmentHashMode`) —
  // comparing across modes never matches.
  return alignmentSourceHash(live, mode) === meta.sourceHash;
}
